import type { DisplayFormat } from '@/shared/utils/displayFormat';
import type {
  AppNotification,
  NotificationCounts,
  NotificationTab,
} from '../../../services/notificationsApi';
import { groupNotifications } from './notificationGroups';
import type { NotificationRow as Row } from './notificationGroups';
import { NotificationRow } from './NotificationRow';
import {
  countForTab,
  emptyHeadingFor,
  NOTIFICATION_TABS,
  subtitleFor,
} from './notificationLook';

interface NotificationPanelProps {
  items: AppNotification[];
  counts: NotificationCounts;
  tab: NotificationTab;
  loading: boolean;
  failed: boolean;
  markingAll: boolean;
  /** The entity's date pattern, time pattern and zone; null until resolved, which falls back to defaults. */
  displayFormat: DisplayFormat | null;
  onSelectTab: (tab: NotificationTab) => void;
  onOpenNotification: (row: Row) => void;
  onMarkAllRead: () => void;
  onClose: () => void;
}

/**
 * The drawer behind the bell: a subtitle, three tabs with their counts, and the grouped list.
 *
 * <p>Presentation only. It is given everything it draws and decides nothing about what the server holds —
 * which tab is open, what each one costs to fetch and what a click does all belong to `useNotifications`.
 */
export function NotificationPanel({
  items,
  counts,
  tab,
  loading,
  failed,
  markingAll,
  displayFormat,
  onSelectTab,
  onOpenNotification,
  onMarkAllRead,
  onClose,
}: NotificationPanelProps) {
  const groups = groupNotifications(items, displayFormat ?? {});
  const empty = !loading && !failed && groups.length === 0;

  return (
    <div className="header__notify-panel" role="dialog" aria-label="Notifications">
      <div className="header__notify-head">
        <span className="header__notify-head-icon" aria-hidden="true">
          <i className="pi pi-bell" />
        </span>
        <span className="header__notify-head-text">
          <span className="header__notify-head-title">Notifications</span>
          <span className="header__notify-subtitle">
            {subtitleFor(counts.unread, counts.actionRequired)}
          </span>
        </span>
        {/*
          Offered only when it would do something. A "Mark all as read" sitting next to an empty badge is a
          control that cannot be told apart from one that is broken.
        */}
        {counts.unread > 0 ? (
          <button
            type="button"
            className="header__notify-readall"
            onClick={onMarkAllRead}
            disabled={markingAll}
          >
            Mark all as read
          </button>
        ) : null}
        <button type="button" className="header__notify-close" aria-label="Close" onClick={onClose}>
          <i className="pi pi-times" aria-hidden="true" />
        </button>
      </div>

      <div className="header__notify-tabs" role="tablist" aria-label="Notification filters">
        {NOTIFICATION_TABS.map(({ tab: value, label }) => {
          const active = value === tab;
          const count = countForTab(value, counts);
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={active}
              className={`header__notify-tab${active ? ' header__notify-tab--active' : ''}`}
              onClick={() => {
                onSelectTab(value);
              }}
            >
              {label}
              {/*
                The count comes from /counts, never from items.length: the list is one page of twenty and
                the count is the whole tab, so counting what arrived would cap every label at 20.
              */}
              {count > 0 ? <span className="header__notify-tab-count">{count}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="header__notify-body">
        {loading ? (
          <div className="header__notify-state">
            <i className="pi pi-spin pi-spinner" aria-hidden="true" /> Loading…
          </div>
        ) : failed ? (
          /* Said plainly. An error drawn as an empty list reads as "you have none", which is a lie. */
          <div className="header__notify-state">Notifications could not be loaded.</div>
        ) : empty ? (
          <div className="header__notify-empty">
            <span className="header__notify-empty-icon" aria-hidden="true">
              <i className="pi pi-bell-slash" />
            </span>
            <div className="header__notify-empty-title">{emptyHeadingFor(tab)}</div>
            <div className="header__notify-empty-text">
              New updates on BIDs, approvals and imports will appear here.
            </div>
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.label}>
              <div className="header__notify-group">{group.label}</div>
              {group.items.map((row) => (
                <NotificationRow
                  key={row.notification.id}
                  row={row}
                  onOpen={onOpenNotification}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
